/**
 * useTaskInstances Hook
 *
 * Provides data access for task instances - the generated tasks for specific batches.
 * Includes functions to query tasks by date, batch, or status, and to complete/skip tasks.
 *
 * US-PE-002a: Process Management Data Layer
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Hook to fetch tasks for a specific date
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Object} options - Additional options
 * @param {string} options.status - Filter by status ('pending', 'completed', 'skipped', or null for all)
 * @returns {Object} { tasks, loading, error, refetch }
 */
export function useTasksForDate(date, options = {}) {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { status = null } = options

  const fetchTasks = useCallback(async () => {
    if (!date) {
      setTasks([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('task_instances')
        .select(`
          *,
          event_tasks (
            id,
            name,
            description,
            estimated_minutes,
            is_required,
            phase_events (
              id,
              name,
              event_type,
              process_phases (
                id,
                name,
                production_processes (
                  id,
                  name
                )
              )
            )
          ),
          production_batches (
            id,
            batch_code,
            current_state,
            strains (
              id,
              name,
              code
            )
          ),
          session_types (
            id,
            name,
            colour
          ),
          timetable_sessions (
            id,
            start_time,
            end_time,
            location
          )
        `)
        .eq('scheduled_date', date)
        .order('created_at', { ascending: true })

      if (status) {
        query = query.eq('status', status)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      setTasks(data || [])
    } catch (err) {
      console.error('[useTasksForDate] Error:', err)
      setError(err.message)
      setTasks([])
    } finally {
      setLoading(false)
    }
  }, [date, status])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  return {
    tasks,
    loading,
    error,
    refetch: fetchTasks
  }
}

/**
 * Hook to fetch tasks due today (using the tasks_due_today view)
 * @returns {Object} { tasks, loading, error, refetch }
 */
export function useTasksDueToday() {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchTasks = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('tasks_due_today')
        .select('*')

      if (fetchError) {
        throw fetchError
      }

      setTasks(data || [])
    } catch (err) {
      console.error('[useTasksDueToday] Error:', err)
      setError(err.message)
      setTasks([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  return {
    tasks,
    loading,
    error,
    refetch: fetchTasks
  }
}

/**
 * Hook to fetch all tasks for a specific batch
 * @param {string} batchId - The batch UUID
 * @param {Object} options - Additional options
 * @param {string} options.status - Filter by status
 * @returns {Object} { tasks, loading, error, refetch }
 */
export function useTasksForBatch(batchId, options = {}) {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { status = null } = options

  const fetchTasks = useCallback(async () => {
    if (!batchId) {
      setTasks([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('task_instances')
        .select(`
          *,
          event_tasks (
            id,
            name,
            description,
            estimated_minutes,
            is_required,
            phase_events (
              id,
              name,
              event_type,
              process_phases (
                id,
                name
              )
            )
          )
        `)
        .eq('batch_id', batchId)
        .order('scheduled_date', { ascending: true })

      if (status) {
        query = query.eq('status', status)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      setTasks(data || [])
    } catch (err) {
      console.error('[useTasksForBatch] Error:', err)
      setError(err.message)
      setTasks([])
    } finally {
      setLoading(false)
    }
  }, [batchId, status])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  return {
    tasks,
    loading,
    error,
    refetch: fetchTasks
  }
}

/**
 * Hook to fetch tasks within a date range
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {Object} options - Additional options
 * @param {string} options.status - Filter by status
 * @param {string} options.batchId - Filter by batch
 * @returns {Object} { tasks, loading, error, refetch }
 */
export function useTasksInDateRange(startDate, endDate, options = {}) {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { status = null, batchId = null } = options

  const fetchTasks = useCallback(async () => {
    if (!startDate || !endDate) {
      setTasks([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('task_instances')
        .select(`
          *,
          event_tasks (
            id,
            name,
            description,
            estimated_minutes,
            is_required,
            phase_events (
              id,
              name,
              event_type,
              process_phases (
                id,
                name,
                production_processes (
                  id,
                  name
                )
              )
            )
          ),
          production_batches (
            id,
            batch_code,
            current_state,
            strains (
              id,
              name,
              code
            )
          ),
          session_types (
            id,
            name,
            colour
          ),
          timetable_sessions (
            id,
            start_time,
            end_time,
            location
          )
        `)
        .gte('scheduled_date', startDate)
        .lte('scheduled_date', endDate)
        .order('scheduled_date', { ascending: true })

      if (status) {
        query = query.eq('status', status)
      }

      if (batchId) {
        query = query.eq('batch_id', batchId)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      setTasks(data || [])
    } catch (err) {
      console.error('[useTasksInDateRange] Error:', err)
      setError(err.message)
      setTasks([])
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, status, batchId])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  return {
    tasks,
    loading,
    error,
    refetch: fetchTasks
  }
}

/**
 * Hook to get task statistics (counts by status)
 * @param {Object} options - Filter options
 * @param {string} options.batchId - Filter by batch
 * @param {string} options.date - Filter by date
 * @returns {Object} { stats, loading, error, refetch }
 */
export function useTaskStats(options = {}) {
  const [stats, setStats] = useState({ pending: 0, completed: 0, skipped: 0, total: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { batchId = null, date = null } = options

  const fetchStats = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('task_instances')
        .select('status')

      if (batchId) {
        query = query.eq('batch_id', batchId)
      }

      if (date) {
        query = query.eq('scheduled_date', date)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      const counts = (data || []).reduce((acc, task) => {
        acc[task.status] = (acc[task.status] || 0) + 1
        acc.total += 1
        return acc
      }, { pending: 0, completed: 0, skipped: 0, total: 0 })

      setStats(counts)
    } catch (err) {
      console.error('[useTaskStats] Error:', err)
      setError(err.message)
      setStats({ pending: 0, completed: 0, skipped: 0, total: 0 })
    } finally {
      setLoading(false)
    }
  }, [batchId, date])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  return {
    stats,
    loading,
    error,
    refetch: fetchStats
  }
}

/**
 * Hook providing mutation functions for task instances
 * @returns {Object} { completeTask, skipTask, updateTask, generateTasksForBatch, updating, updateError }
 */
export function useTaskInstanceMutations() {
  const [updating, setUpdating] = useState(false)
  const [updateError, setUpdateError] = useState(null)

  /**
   * Mark a task as completed
   * @param {string} taskId - Task instance UUID
   * @param {string} notes - Optional completion notes
   */
  const completeTask = async (taskId, notes = null) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser()

      const { data, error: updateErr } = await supabase
        .from('task_instances')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          completed_by: user?.id || null,
          notes: notes
        })
        .eq('id', taskId)
        .select()
        .single()

      if (updateErr) {
        throw updateErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[useTaskInstanceMutations] Complete error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Mark a task as skipped
   * @param {string} taskId - Task instance UUID
   * @param {string} reason - Required reason for skipping
   */
  const skipTask = async (taskId, reason) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      if (!reason) {
        throw new Error('A reason is required when skipping a task')
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser()

      const { data, error: updateErr } = await supabase
        .from('task_instances')
        .update({
          status: 'skipped',
          completed_at: new Date().toISOString(),
          completed_by: user?.id || null,
          skipped_reason: reason
        })
        .eq('id', taskId)
        .select()
        .single()

      if (updateErr) {
        throw updateErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[useTaskInstanceMutations] Skip error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Update task instance (e.g., reschedule, add notes)
   * @param {string} taskId - Task instance UUID
   * @param {Object} updates - Fields to update
   */
  const updateTask = async (taskId, updates) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: updateErr } = await supabase
        .from('task_instances')
        .update(updates)
        .eq('id', taskId)
        .select()
        .single()

      if (updateErr) {
        throw updateErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[useTaskInstanceMutations] Update error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Reset a task back to pending status
   * @param {string} taskId - Task instance UUID
   */
  const resetTask = async (taskId) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: updateErr } = await supabase
        .from('task_instances')
        .update({
          status: 'pending',
          completed_at: null,
          completed_by: null,
          skipped_reason: null
        })
        .eq('id', taskId)
        .select()
        .single()

      if (updateErr) {
        throw updateErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[useTaskInstanceMutations] Reset error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Generate task instances for a batch
   * Uses the generate_batch_tasks database function
   * @param {string} batchId - Batch UUID
   * @param {string} startDate - Start date for task scheduling (YYYY-MM-DD)
   */
  const generateTasksForBatch = async (batchId, startDate) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: rpcErr } = await supabase
        .rpc('generate_batch_tasks', {
          p_batch_id: batchId,
          p_start_date: startDate
        })

      if (rpcErr) {
        throw rpcErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[useTaskInstanceMutations] Generate error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Delete a task instance
   * @param {string} taskId - Task instance UUID
   */
  const deleteTask = async (taskId) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { error: deleteErr } = await supabase
        .from('task_instances')
        .delete()
        .eq('id', taskId)

      if (deleteErr) {
        throw deleteErr
      }

      return { error: null }
    } catch (err) {
      console.error('[useTaskInstanceMutations] Delete error:', err)
      setUpdateError(err.message)
      return { error: err }
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Bulk complete multiple tasks
   * @param {Array<string>} taskIds - Array of task instance UUIDs
   * @param {string} notes - Optional notes for all tasks
   */
  const bulkComplete = async (taskIds, notes = null) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data: { user } } = await supabase.auth.getUser()

      const { data, error: updateErr } = await supabase
        .from('task_instances')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          completed_by: user?.id || null,
          notes: notes
        })
        .in('id', taskIds)
        .select()

      if (updateErr) {
        throw updateErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[useTaskInstanceMutations] Bulk complete error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  return {
    completeTask,
    skipTask,
    updateTask,
    resetTask,
    generateTasksForBatch,
    deleteTask,
    bulkComplete,
    updating,
    updateError
  }
}

/**
 * Hook for previewing override impact before applying
 * Calls preview_override_impact RPC to show downstream effects
 *
 * US-PPL-001 Phase 5: Override Impact Preview
 *
 * @returns {Object} { previewOverride, clearPreview, preview, loading, error }
 */
export function useOverridePreview() {
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const previewOverride = useCallback(async (entityType, entityId, field, newValue) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: rpcError } = await supabase.rpc('preview_override_impact', {
        p_entity_type: entityType,
        p_entity_id: entityId,
        p_field: field,
        p_new_value: String(newValue)
      })

      if (rpcError) throw rpcError
      setPreview(data)
      return data
    } catch (err) {
      console.error('[useOverridePreview] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const clearPreview = useCallback(() => {
    setPreview(null)
    setError(null)
  }, [])

  return { previewOverride, clearPreview, preview, loading, error }
}

export default useTasksForDate
